import IconChevron, {
    chevronDirections,
} from './svg/IconChevron';
import LabelledTickbox from './LabelledTickbox';
import React, { useEffect, useRef, useState, Fragment } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';

const Row = ({ column, onClick, value = false }) => {
    const handleClick = () => onClick(column);
    return (
        <div
            id={column.field}
            style={{ display: 'flex', boxSizing: 'border-box' }}
        >
            <LabelledTickbox
                id={`${column.field}Tickbox`}
                onClick={handleClick}
                value={value}
                title={column.caption}
                labelStyle={{
                    color: '#414141',
                    fontFamily: 'Roboto',
                    fontSize: 14,
                    fontWeight: 500
                }}
                gap={10}
                reverseElements
            />
        </div>
    );
}; // Row end

const Tongue = ({
    expanded = true,
    boundingRect = {},
    columns = [],
    activeColumns = [],
    onChange,
    onClickOutside,
    style
}) => {
    const ref = useRef(null);
    const [widthOffset, setWidthOffset] = useState(0);
    // Position calculation
    useEffect(() => {
        const width = ref?.current?.getBoundingClientRect()?.width ?? 0;
        setWidthOffset(
            width > 0
                ? ((ref?.current?.getBoundingClientRect()?.width ?? 0) -
                      boundingRect.width) /
                      2
                : 0
        );
    }, [expanded]);
    // Click outside handler
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (ref.current && !ref.current.contains(event.target)) {
                onClickOutside(event, true);
            }
        };
        if (expanded) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [ref, expanded]);

    const onClick = (column) => {
        const mutar = [...activeColumns];
        const i = mutar.findIndex((c) => c.field === column.field);
        if (i !== -1) {
            mutar.splice(i, 1);
        } else {
            const j = columns.findIndex((c) => c.field === column.field);
            mutar.splice(j, 0, columns[j]);
        }
        if (typeof onChange === 'function') {
            onChange(mutar);
        } else {
            // eslint-disable-next-line no-console
            console.warn('onChange is not a function');
        }
    };

    return !expanded
        ? null
        : createPortal(
            <div
                id='columnFilterTongue'
                ref={ref}
                style={{
                    boxSizing: 'border-box',
                    minWidth: boundingRect.width,
                    top:
                          boundingRect.top +
                          boundingRect.height +
                          window.scrollY,
                    left: boundingRect.left + window.scrollX - widthOffset,
                    border: '1px solid #cccccc',
                    borderRadius: '0 0 15px 15px',
                    backgroundColor: '#ffffff',
                    display: 'grid',
                    gridAutoFlow: 'row',
                    gridAutoRows: 27,
                    position: 'absolute',
                    padding: '5px 10px',
                    ...style
                }}
            >
                {columns.map((c, i) => (
                    !c.hidden && (
                        <Row
                            key={i}
                            column={c}
                            value={activeColumns.includes(c)}
                            onClick={onClick}
                        />
                    )
                ))}
            </div>,
            document.body
        );
}; // Tongue end

const GridColumnFilter = ({ columns = [], activeColumns = [], onChange, style, tongueStyle }) => {
    const ref = useRef(null);
    const [expanded, setExpanded] = useState(false);
    /**
     * click event handler for this input of this component
     * @param {Event} event - click event
     * @param {boolean} isOutsideClick - whether the click came from the outside click handler in the tongue
     */
    const onExpandClick = (event, isOutsideClick = false) => {
        if (ref.current.contains(event.target) && isOutsideClick) {
            return;
        }
        setExpanded(!expanded);
    };

    return (
        <Fragment>
            <div
                ref={ref}
                id='gridColumnFilter'
                onClick={onExpandClick}
                style={{
                    display: 'flex',
                    gap: 10,
                    boxSizing: 'border-box',
                    cursor: 'pointer',
                    width: 'max-content',
                    ...style
                }}
            >
                <span
                    id='filterTitle'
                    style={{
                        fontFamily: 'Roboto',
                        fontSize: 14,
                        fontWeight: 500
                    }}
                >
                    Filter
                </span>
                <IconChevron
                    id='filterChevron'
                    size={14}
                    style={{ fill: 'rgb(65, 65, 65)' }}
                    direction={
                        expanded
                            ? chevronDirections.LEFT
                            : chevronDirections.DOWN
                    }
                />
            </div>
            <Tongue
                expanded={expanded}
                boundingRect={ref?.current?.getBoundingClientRect()}
                columns={columns}
                activeColumns={activeColumns}
                onChange={onChange}
                onClickOutside={onExpandClick}
                style={tongueStyle}
            />
        </Fragment>
    );
}; // GridColumnFilter end

GridColumnFilter.defaultProps = {
    columns: [],
    activeColumns: []
};

GridColumnFilter.propTypes = {
    columns: PropTypes.array,
    activeColumns: PropTypes.array,
    onChange: PropTypes.func,
    style: PropTypes.object,
    tongueStyle: PropTypes.object
};

export default GridColumnFilter;
